import {
	createEmptyInformationGatheringDraft,
	type InformationGatheringDraft,
	type InformationGatheringPatientIdentity,
	isInformationGatheringComplete,
} from '@feature/information-gathering/information-gathering.model';
import type { InformationGatheringEntry } from '@feature/information-gathering/information-gathering-navigation';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useReducer, useRef, useState } from 'react';

export type SelfInformationGatheringStep = 'self-details' | 'self-contact' | 'location';
export type RepresentativeInformationGatheringStep =
	| 'representative-details'
	| 'representative-contact'
	| 'patient-details'
	| 'patient-contact'
	| 'location';
export type InformationGatheringStep = SelfInformationGatheringStep | RepresentativeInformationGatheringStep;

const SELF_STEPS: readonly SelfInformationGatheringStep[] = ['self-details', 'self-contact', 'location'];
const REPRESENTATIVE_STEPS: readonly RepresentativeInformationGatheringStep[] = [
	'representative-details',
	'representative-contact',
	'patient-details',
	'patient-contact',
	'location',
];

const SELF_STEPS_WITHOUT_DETAILS: readonly SelfInformationGatheringStep[] = ['self-contact', 'location'];

export function getInformationGatheringSteps(entry: InformationGatheringEntry): readonly InformationGatheringStep[] {
	if (entry.role === 'representative') return REPRESENTATIVE_STEPS;
	return entry.destination === 'red-robin' || entry.preselectedSexAssignedAtBirth !== null ? SELF_STEPS_WITHOUT_DETAILS : SELF_STEPS;
}

interface JourneyState {
	draft: InformationGatheringDraft;
	patientIdentity: InformationGatheringPatientIdentity;
	stepIndex: number;
}

type JourneyAction = { type: 'save-draft'; draft: InformationGatheringDraft } | { type: 'next' } | { type: 'back' };
type JourneyStateAction = JourneyAction | { type: 'save-patient-identity'; patientIdentity: InformationGatheringPatientIdentity };

function createInitialJourneyState(entry: InformationGatheringEntry): JourneyState {
	const draft = createEmptyInformationGatheringDraft();
	if (entry.preselectedSexAssignedAtBirth !== null) {
		draft.patient.sexAssignedAtBirth = entry.preselectedSexAssignedAtBirth;
	}

	return {
		draft,
		patientIdentity: entry.patientIdentity,
		stepIndex: 0,
	};
}

function journeyReducer(state: JourneyState, action: JourneyStateAction): JourneyState {
	switch (action.type) {
		case 'save-draft':
			return { ...state, draft: action.draft };
		case 'save-patient-identity':
			return { ...state, patientIdentity: action.patientIdentity };
		case 'next':
			return { ...state, stepIndex: state.stepIndex + 1 };
		case 'back':
			return { ...state, stepIndex: Math.max(0, state.stepIndex - 1) };
	}
}

interface InformationGatheringJourneyContextValue {
	role: InformationGatheringEntry['role'];
	destination: InformationGatheringEntry['destination'];
	shouldAskSexAssignedAtBirth: boolean;
	step: InformationGatheringStep;
	draft: InformationGatheringDraft;
	patientIdentity: InformationGatheringPatientIdentity;
	saveDraft(draft: InformationGatheringDraft): void;
	savePatientIdentity(patientIdentity: InformationGatheringPatientIdentity): void;
	goNext(): void;
	goBack(event: Event): void;
	complete(draft: InformationGatheringDraft): Promise<void>;
	isSaving: boolean;
	saveError: boolean;
}

const InformationGatheringJourneyContext = createContext<InformationGatheringJourneyContextValue | null>(null);

export function InformationGatheringJourneyProvider({
	entry,
	onComplete,
	children,
}: {
	entry: InformationGatheringEntry;
	onComplete(
		entry: InformationGatheringEntry,
		draft: InformationGatheringDraft,
		patientIdentity: InformationGatheringPatientIdentity,
	): Promise<boolean>;
	children: ReactNode;
}) {
	const [state, dispatch] = useReducer(journeyReducer, entry, createInitialJourneyState);
	const [isSaving, setIsSaving] = useState(false);
	const isSavingRef = useRef(false);
	const [saveError, setSaveError] = useState(false);
	const steps = getInformationGatheringSteps(entry);
	const step = steps[state.stepIndex]!;

	const saveDraft = useCallback((draft: InformationGatheringDraft) => dispatch({ type: 'save-draft', draft }), []);
	const savePatientIdentity = useCallback(
		(patientIdentity: InformationGatheringPatientIdentity) => dispatch({ type: 'save-patient-identity', patientIdentity }),
		[],
	);
	const goNext = useCallback(() => dispatch({ type: 'next' }), []);
	const goBack = useCallback(
		(event: Event) => {
			if (state.stepIndex > 0) {
				event.preventDefault();
				dispatch({ type: 'back' });
			}
		},
		[state.stepIndex],
	);
	const complete = useCallback(
		async (draft: InformationGatheringDraft) => {
			if (!isInformationGatheringComplete(draft, entry.role, state.patientIdentity, entry.destination) || isSavingRef.current) return;

			isSavingRef.current = true;
			setIsSaving(true);
			setSaveError(false);
			const saved = await onComplete(entry, draft, state.patientIdentity);
			if (!saved) {
				setSaveError(true);
				isSavingRef.current = false;
				setIsSaving(false);
			}
		},
		[entry, onComplete, state.patientIdentity],
	);

	const value = useMemo(
		() => ({
			role: entry.role,
			destination: entry.destination,
			shouldAskSexAssignedAtBirth: entry.destination !== 'red-robin' && entry.preselectedSexAssignedAtBirth === null,
			step,
			draft: state.draft,
			patientIdentity: state.patientIdentity,
			saveDraft,
			savePatientIdentity,
			goNext,
			goBack,
			complete,
			isSaving,
			saveError,
		}),
		[
			complete,
			entry.role,
			entry.destination,
			entry.preselectedSexAssignedAtBirth,
			goBack,
			goNext,
			isSaving,
			saveDraft,
			saveError,
			savePatientIdentity,
			state.draft,
			state.patientIdentity,
			step,
		],
	);

	return <InformationGatheringJourneyContext.Provider value={value}>{children}</InformationGatheringJourneyContext.Provider>;
}

export function useInformationGatheringJourney(): InformationGatheringJourneyContextValue {
	const journey = useContext(InformationGatheringJourneyContext);

	if (journey === null) {
		throw new Error('useInformationGatheringJourney must be used within InformationGatheringJourneyProvider');
	}

	return journey;
}
